import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { authClient } from "@/lib/auth/auth-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";

export const Route = createFileRoute("/auth/mfa-verify")({
  head: () => ({ meta: [{ title: "Verify two-factor — Heallexa" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: MfaVerifyPage,
});

/**
 * Second-factor challenge during login.
 *
 * An enrolled user who signs in does NOT receive a session — Cognito returns a
 * SOFTWARE_TOKEN_MFA challenge and the auth-client redirects here (the pending
 * challenge is held in a short-lived httpOnly cookie). So there is no `user`
 * yet; we must NOT gate this page on an authenticated session.
 *
 *   verifyTotp({ code }) — completes the challenge; on success a session is set.
 *
 * Cognito TOTP has no backup codes (recovery is via admin reset). On success we
 * hard-navigate to /post-login so the new session cookie is picked up.
 */
function MfaVerifyPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Already fully authenticated (e.g. landed here by accident) → move along.
  useEffect(() => {
    if (loading) return;
    if (user) navigate({ to: "/dashboard" as any, replace: true });
  }, [user, loading, navigate]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const { error: err } = await authClient.twoFactor.verifyTotp({ code });
      if (err) {
        setError(err.message ?? "That code didn't match. Try again.");
        return;
      }
      // Session is live — full reload so the cookie reaches the server router.
      if (typeof window !== "undefined") window.location.href = "/post-login";
    } catch (e) {
      setError(e instanceof Error ? e.message : "Verification failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md bg-card border border-border rounded-xl p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-1">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-primary" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Two-factor required</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          Open your authenticator app and enter the 6-digit code for Heallexa.
        </p>

        <div className="space-y-2">
          <Label htmlFor="code">Authenticator code</Label>
          <Input
            id="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="123456"
            className="text-center tracking-[0.4em] text-lg font-mono"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && code.length >= 6) submit();
            }}
          />
        </div>

        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

        <Button onClick={submit} disabled={busy || code.length < 6} className="w-full mt-5">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Verify
        </Button>

        <button
          type="button"
          className="mt-4 text-xs text-muted-foreground hover:text-foreground underline w-full text-center"
          onClick={async () => {
            try {
              await authClient.signOut();
            } catch {
              /* no active session during 2FA challenge — ignore */
            }
            navigate({ to: "/login" });
          }}
        >
          Cancel and sign out
        </button>
      </div>
    </div>
  );
}
